import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { loc } from '@/localization';
import { optionsService } from '@/services/instance/optionsService';
import { saml } from '@/services/instance/saml';
import { getLoginUrl } from '@/utils/getPageUrl';

type SamlIdpRequest = Parameters<(typeof saml)['verifySamlIdp']>[0];
type FormDataType = SamlIdpRequest;

export function useSelectSamlProviderPage() {
    const [searchParams] = useSearchParams();

    const initialEmail = searchParams.get('email') || undefined;
    const initialIdp = searchParams.get('idp') || undefined;

    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        formState: { isSubmitting, errors },
    } = useForm<FormDataType>({
        defaultValues: {
            idp: initialIdp || initialEmail,
        },
    });

    const setErrors = useCallback(() => {
        setError('idp', {
            type: 'value',
            message: loc.Login.InvalidSsoIdProvider,
        });
    }, [setError]);

    const cleanErrors = useCallback(() => {
        clearErrors('idp');
    }, [clearErrors]);

    const submitForm = useCallback(
        async (data: FormDataType) => {
            const idp = data.idp.trim();

            const isValidProvider = await saml.verifySamlIdp({ idp });
            if (isValidProvider) {
                saml.navigateToSamlProvider({ idp });
                return;
            }
            setErrors();
        },
        [setErrors],
    );

    const onFormSubmit = useCallback(
        (ev: React.BaseSyntheticEvent) => {
            void handleSubmit(submitForm)(ev);
        },
        [handleSubmit, submitForm],
    );

    const optionsQuery = optionsService.getQueryArgs();

    const loginUrl = getLoginUrl({
        ...optionsQuery,
        email: initialEmail as string,
    });

    return {
        loginUrl,
        register,
        errors,
        cleanErrors,
        isSubmitting,
        onFormSubmit,
    };
}
